import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Button, Icon, RoleChip, Skeleton, Spinner } from '@w7/ui'
import { useChildren } from '@/app/queries'
import { ROLE_SHORT, type Account } from '@/data/types'

/**
 * Slim outline for the Hierarchy split pane — navigation only.
 *
 * Money and Give/Take live in the detail panel. Flat `role="tree"` so keyboard
 * nav has one visible-row list (same pattern as HierarchyScopePicker).
 */

interface OutlineContextValue {
  expanded: Set<string>
  selectedId: string | null
  toggle: (id: string, open: boolean, username: string, childCount: number) => void
  select: (id: string) => void
  tabbableId: string | null
  setFocusId: (id: string) => void
  highlightId: string | null
}

const OutlineContext = createContext<OutlineContextValue | null>(null)
const useOutline = (): OutlineContextValue => {
  const value = useContext(OutlineContext)
  if (!value) throw new Error('Outline rows must render inside DownlineOutline')
  return value
}

export interface DownlineOutlineProps {
  roots: Account[]
  loading: boolean
  expanded: Set<string>
  selectedId: string | null
  onExpandedChange: (id: string, open: boolean) => void
  onSelect: (id: string) => void
  landOn?: string
  empty: ReactNode
}

export function DownlineOutline({
  roots,
  loading,
  expanded,
  selectedId,
  onExpandedChange,
  onSelect,
  landOn,
  empty,
}: DownlineOutlineProps) {
  const listRef = useRef<HTMLUListElement>(null)
  const [focusId, setFocusId] = useState<string | null>(null)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const landedRef = useRef<string | null>(null)

  const itemsOf = useCallback(
    (): HTMLElement[] =>
      Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="treeitem"][data-id]') ?? []),
    [],
  )

  const toggle = useCallback(
    (id: string, open: boolean, username: string, childCount: number) => {
      onExpandedChange(id, open)
      setAnnouncement(
        open
          ? `${username} expanded, ${childCount} ${childCount === 1 ? 'child' : 'children'}`
          : `${username} collapsed`,
      )
      if (!open) {
        setFocusId(id)
        if (listRef.current?.contains(document.activeElement)) {
          window.requestAnimationFrame(() => {
            itemsOf()
              .find((row) => row.dataset.id === id)
              ?.focus()
          })
        }
      }
    },
    [onExpandedChange, itemsOf],
  )

  useEffect(() => {
    if (!landOn || landedRef.current === landOn) return
    const target = itemsOf().find((row) => row.dataset.id === landOn)
    if (!target) return
    landedRef.current = landOn
    target.scrollIntoView({ block: 'nearest' })
    target.focus()
    setHighlightId(landOn)
    const timer = window.setTimeout(() => setHighlightId(null), 2000)
    return () => window.clearTimeout(timer)
  })

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const target = event.target as HTMLElement
    const current = target.closest?.('[role="treeitem"][data-id]') as HTMLElement | null
    if (!current) return

    const all = itemsOf()
    const index = all.indexOf(current)
    const id = current.dataset.id ?? ''
    const step = (to: HTMLElement | undefined) => {
      if (!to) return
      event.preventDefault()
      to.focus()
    }

    switch (event.key) {
      case 'ArrowDown':
        step(all[index + 1])
        return
      case 'ArrowUp':
        step(all[index - 1])
        return
      case 'Home':
        step(all[0])
        return
      case 'End':
        step(all[all.length - 1])
        return
      case 'ArrowRight': {
        if (current.dataset.expandable !== 'true') return
        event.preventDefault()
        if (current.getAttribute('aria-expanded') !== 'true') {
          toggle(id, true, current.dataset.username ?? id, Number(current.dataset.childcount ?? 0))
          return
        }
        if (current.getAttribute('aria-busy') === 'true') return
        const next = all[index + 1]
        if (next && next.dataset.parent === id) next.focus()
        return
      }
      case 'ArrowLeft': {
        event.preventDefault()
        if (current.getAttribute('aria-expanded') === 'true') {
          toggle(id, false, current.dataset.username ?? id, Number(current.dataset.childcount ?? 0))
          return
        }
        const parent = all.find((row) => row.dataset.id === current.dataset.parent)
        if (parent) parent.focus()
        return
      }
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSelect(id)
        return
      default:
    }
  }

  if (!loading && roots.length === 0) {
    return <div className="w7-dl-outline">{empty}</div>
  }

  const tabbableId = focusId ?? selectedId ?? roots[0]?.id ?? null

  return (
    <OutlineContext.Provider
      value={{
        expanded,
        selectedId,
        toggle,
        select: onSelect,
        tabbableId,
        setFocusId,
        highlightId,
      }}
    >
      <div className="w7-dl-outline">
        <div className="w7-dl-outline-head">
          <span className="w7-micro">Hierarchy</span>
        </div>
        <ul
          ref={listRef}
          className="w7-dl-outline-tree"
          role="tree"
          aria-label="Your downline hierarchy"
          onKeyDown={onKeyDown}
        >
          {loading
            ? Array.from({ length: 8 }, (_, i) => (
                <li key={`skeleton-${i}`} className="w7-dl-outline-skel" aria-hidden="true">
                  <Skeleton width="70%" />
                </li>
              ))
            : roots.map((row, index) => (
                <OutlineNode
                  key={row.id}
                  row={row}
                  depth={0}
                  posinset={index + 1}
                  setsize={roots.length}
                />
              ))}
        </ul>
      </div>
      <span className="w7-sr" aria-live="polite">
        {announcement}
      </span>
    </OutlineContext.Provider>
  )
}

function OutlineNode({
  row,
  depth,
  posinset,
  setsize,
}: {
  row: Account
  depth: number
  posinset: number
  setsize: number
}) {
  const { expanded, selectedId, toggle, select, tabbableId, setFocusId, highlightId } = useOutline()
  const open = expanded.has(row.id)
  const hasChildren = row.childCount > 0
  const children = useChildren(open && hasChildren ? row.id : undefined)
  const loaded = children.data
  const selected = selectedId === row.id

  return (
    <>
      <li
        role="treeitem"
        data-id={row.id}
        data-parent={row.parentId ?? ''}
        data-username={row.username}
        data-childcount={row.childCount}
        data-expandable={hasChildren ? 'true' : undefined}
        data-selected={selected ? 'true' : undefined}
        data-highlight={highlightId === row.id ? 'true' : undefined}
        aria-level={depth + 1}
        aria-posinset={posinset}
        aria-setsize={setsize}
        aria-expanded={hasChildren ? open : undefined}
        aria-selected={selected}
        aria-busy={children.isLoading ? true : undefined}
        tabIndex={tabbableId === row.id ? 0 : -1}
        className="w7-dl-outline-item"
        style={{ '--w7-depth': depth } as CSSProperties}
        onFocus={() => setFocusId(row.id)}
        onClick={() => select(row.id)}
      >
        <span className="w7-dl-outline-row">
          {hasChildren ? (
            <button
              type="button"
              className="w7-dl-outline-toggle"
              tabIndex={-1}
              aria-hidden="true"
              onClick={(event) => {
                event.stopPropagation()
                toggle(row.id, !open, row.username, row.childCount)
              }}
            >
              {children.isLoading ? (
                <Spinner size={13} />
              ) : (
                <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
              )}
            </button>
          ) : (
            <span className="w7-dl-outline-spacer" aria-hidden="true" />
          )}
          <span className="w7-dl-outline-ident">
            <span className="w7-dl-outline-name">{row.username}</span>
            <RoleChip role={row.role} label={ROLE_SHORT[row.role]} />
          </span>
        </span>
      </li>

      {open && hasChildren ? (
        <>
          {children.isLoading ? (
            <li
              className="w7-dl-outline-sub"
              aria-hidden="true"
              style={{ '--w7-depth': depth + 1 } as CSSProperties}
            >
              <Skeleton width="50%" />
            </li>
          ) : children.isError ? (
            <li className="w7-dl-outline-sub" style={{ '--w7-depth': depth + 1 } as CSSProperties}>
              <Icon name="warning" size={13} />
              {(children.error as Error)?.message ?? 'Could not load'}
              <Button size="sm" variant="ghost" icon="refresh" onClick={() => children.refetch()}>
                Retry
              </Button>
            </li>
          ) : loaded && loaded.length === 0 ? (
            <li className="w7-dl-outline-sub" style={{ '--w7-depth': depth + 1 } as CSSProperties}>
              No direct downline
            </li>
          ) : loaded ? (
            loaded.map((child, index) => (
              <OutlineNode
                key={child.id}
                row={child}
                depth={depth + 1}
                posinset={index + 1}
                setsize={loaded.length}
              />
            ))
          ) : null}
        </>
      ) : null}
    </>
  )
}
